import { RG_TYPE, getRgObjectsOfType } from "@project-rouge/rg-core";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { when } from "mobx";
import { useCurrencySymbol } from "src/hooks/useCurrencySymbol";
import {
  BKI_CITY_LIST,
  type BkiCity,
  type BkiSizeBracket,
  type BkiStandard,
} from "src/service/cost/data/bki";
import { computeGerman } from "src/screens/cost-engine-compare-engine";
import {
  COST_TRACKING_SETTINGS,
  COST_TRACKING_VARIANTS,
  type CostTrackingVariant,
} from "src/screens/cost-tracking-variants";
import {
  createCostingViewForBuilding,
  createCostingViewForTotal,
  type CostingView,
  type CostingViewSummary,
} from "src/service/cost/utility/custom-view";
import { DesignsGenerator } from "src/types/DesignsGenerator";
import type { GeneratedItem } from "src/types/GeneratedItem";
import type { RgBuilding } from "src/types/RgCorePackage";
import { CreateBoxWorld } from "src/utils/CreateBoxWorld";
import { GetCostDataHash } from "src/utils/GetCostDataHash";
import { RenderRgWorld } from "src/utils/RenderRgWorld";
import { RgWorldFromZip } from "src/utils/RgWorldFromZip";
import { RgWorldToBeCostPayload } from "src/utils/RgWorldToBeCostPayload";
import { calculateCost, clearCostDataCache } from "src/workers/cost-engine";

// A frozen set of building variants. The procedural generator is seeded
// (briefIndex = 0 always), so each (side, floors) pair always yields the
// same building. The cost data is what varies — that's the point of this
// screen: edit /public/cost_data/*.json, click "Re-run cost calcs", see how
// the same buildings get different cost outputs.
//
// The aggregator has a minimum viable footprint — modules need to fit with
// corridors, cores, and zone setback. Dimensions below ~22m fail to generate
// (an empty design × 50 invalidations). Tall buildings need more footprint
// as cores grow. Sizes below are picked to all generate cleanly.
//
// All variants store `length` + `width` (a square just has length === width)
// — `kind` is only a tag for labeling. The 5 rectangular entries diversify
// aspect ratio so cost-data changes are exercised against varied geometry.
//
// VARIANTS + COST_SETTINGS live in `cost-tracking-variants.ts` so the
// detail screen can reference variants by index and produce the same
// building (the generator is seeded).

type TileStatus = "pending" | "generating" | "rendering" | "done" | "error";

type Tile = {
  variant: CostTrackingVariant;
  variantIndex: number;
  status: TileStatus;
  item: GeneratedItem | null;
  thumbnail3D: string | null;
  costMetrics: CostingView | null;
  costMetricsPerBuilding: CostingViewSummary[] | null;
  error: string | null;
};

const initialTiles = (): Tile[] =>
  COST_TRACKING_VARIANTS.map((v, i) => ({
    variant: v,
    variantIndex: i,
    status: "pending",
    item: null,
    thumbnail3D: null,
    costMetrics: null,
    costMetricsPerBuilding: null,
    error: null,
  }));

export const CostTrackingScreen = observer(function CostTrackingScreen() {
  const [tiles, setTiles] = useState<Tile[]>(initialTiles);
  const [costDataHash, setCostDataHash] = useState<string | null>(null);
  const [recalcRunning, setRecalcRunning] = useState(false);
  const cancelledRef = useRef(false);
  const currency = useCurrencySymbol();
  const [bkiCity, setBkiCity] = useState<BkiCity | null>(null);
  const [bkiSize, setBkiSize] = useState<BkiSizeBracket>(">19");
  const [bkiStandard, setBkiStandard] = useState<BkiStandard>("medium");
  const [inflation, setInflation] = useState(1);
  const [inflationText, setInflationText] = useState("1");

  const setTile = (idx: number, patch: Partial<Tile>) => {
    setTiles((prev) => prev.map((t, i) => (i === idx ? { ...t, ...patch } : t)));
  };

  // Initial cost-data hash for the header.
  useEffect(() => {
    GetCostDataHash().then(setCostDataHash);
  }, []);

  // Auto-generate on mount: one DesignsGenerator reused across variants
  // (sequential — each .generate() terminates the previous worker run).
  useEffect(() => {
    cancelledRef.current = false;
    const generator = new DesignsGenerator({ projectId: "cost-tracking", listSize: 1 });
    (async () => {
      for (let i = 0; i < COST_TRACKING_VARIANTS.length; i++) {
        if (cancelledRef.current) return;
        setTile(i, { status: "generating" });
        const variant = COST_TRACKING_VARIANTS[i];
        try {
          const world = CreateBoxWorld(variant.length, variant.width, variant.floors);
          generator.generate(world);
          await new Promise<void>((resolve) =>
            when(
              () => !generator.isRunning || generator.list.length > 0,
              () => resolve()
            )
          );
          if (cancelledRef.current) return;
          const item = generator.list[0];
          if (!item) {
            setTile(i, { status: "error", error: "Generator produced no option" });
            continue;
          }
          setTile(i, { status: "rendering", item });
          const rgWorld = RgWorldFromZip(item.rgWorldZip);
          const thumb = await RenderRgWorld(rgWorld, [1, -1, 1], 280);
          if (cancelledRef.current) return;
          setTile(i, {
            status: "done",
            thumbnail3D: thumb,
            costMetrics: item.costMetrics,
            costMetricsPerBuilding: item.costMetricsPerBuilding,
          });
        } catch (err) {
          setTile(i, {
            status: "error",
            error: err instanceof Error ? err.message : String(err),
          });
        }
      }
    })();
    return () => {
      cancelledRef.current = true;
      generator.terminate();
    };
  }, []);

  const onRecalc = async () => {
    if (recalcRunning) return;
    setRecalcRunning(true);
    try {
      // Force the engine to refetch the JSON files; otherwise the
      // module-scope promise serves the data loaded on first calc.
      clearCostDataCache();
      const newHash = await GetCostDataHash();
      setCostDataHash(newHash);
      // Re-run cost calcs for every tile that has a cached generated world.
      // We don't regenerate buildings — just re-cost the existing geometry.
      await Promise.all(
        tiles.map(async (tile, i) => {
          if (!tile.item) return;
          const rgWorld = RgWorldFromZip(tile.item.rgWorldZip);
          const buildings = getRgObjectsOfType<RgBuilding>(rgWorld, RG_TYPE.Building);
          const payload = RgWorldToBeCostPayload(rgWorld, buildings);
          const calculation = await calculateCost(payload, COST_TRACKING_SETTINGS);
          const costMetrics = createCostingViewForTotal(calculation);
          const costMetricsPerBuilding = buildings.map(
            (_, bi) => createCostingViewForBuilding(calculation, bi).summary
          );
          setTile(i, { costMetrics, costMetricsPerBuilding });
        })
      );
    } finally {
      setRecalcRunning(false);
    }
  };

  const allDone = tiles.every((t) => t.status === "done" || t.status === "error");

  return (
    <div className="h-screen w-screen overflow-y-auto bg-white text-neutral-8 p-6 font-mono text-xs">
      <header className="flex flex-wrap items-center gap-3 mb-6">
        <h1 className="text-lg font-bold mr-4">Cost tracking</h1>
        <span className="text-neutral-6">
          cost_data hash:{" "}
          <code className="text-neutral-8">{costDataHash ?? "…"}</code>
        </span>
        <button
          type="button"
          onClick={onRecalc}
          disabled={!allDone || recalcRunning}
          className="ml-auto px-3 py-1 rounded bg-neutral-8 text-white hover:bg-neutral-7 disabled:bg-neutral-4 disabled:cursor-not-allowed"
        >
          {recalcRunning ? "Re-running…" : "Re-run cost calcs"}
        </button>
      </header>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="text-neutral-6 text-[10px] uppercase tracking-wide">BKI (DE €)</span>
        <select
          value={bkiCity ?? ""}
          onChange={(e) => setBkiCity(e.target.value ? (e.target.value as BkiCity) : null)}
          className="border border-neutral-3 rounded px-2 py-1 text-xs"
        >
          <option value="">— off</option>
          {BKI_CITY_LIST.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select
          disabled={!bkiCity}
          value={bkiSize}
          onChange={(e) => setBkiSize(e.target.value as BkiSizeBracket)}
          className="border border-neutral-3 rounded px-2 py-1 text-xs disabled:opacity-50"
        >
          <option value="<6">&lt; 6 units</option>
          <option value="6-19">6–19 units</option>
          <option value=">19">&gt; 19 units</option>
        </select>
        <select
          disabled={!bkiCity}
          value={bkiStandard}
          onChange={(e) => setBkiStandard(e.target.value as BkiStandard)}
          className="border border-neutral-3 rounded px-2 py-1 text-xs disabled:opacity-50"
        >
          <option value="simple">simple</option>
          <option value="medium">medium</option>
          <option value="high">high</option>
        </select>
        <span className="text-neutral-6 text-[10px] uppercase tracking-wide ml-2">Inflation ×</span>
        <input
          type="number"
          step="0.01"
          min="0"
          value={inflationText}
          onChange={(e) => {
            const raw = e.target.value;
            setInflationText(raw);
            const n = Number(raw);
            setInflation(raw !== "" && Number.isFinite(n) && n > 0 ? n : 1);
          }}
          className="w-16 border border-neutral-3 rounded px-2 py-1 text-xs text-right"
          title="Inflation factor on KG 200–600 (BKI-rated groups). Excludes land (KG 100) and capital (KG 800). Default 1.0."
        />
      </div>

      <p className="text-neutral-6 mb-4">
        Ten fixed building variants. Same brief always produces the same building (procedural
        generator is seeded). Edit <code>/public/cost_data/*.json</code> then click
        "Re-run cost calcs" to see how the data changes flow through to these cost outputs.
        Pick a BKI city to add a German DIN 276 partial total per tile (KG 300/400 on BGF,
        × inflation factor; resi/commercial split when a building has commercial area).
      </p>

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        {tiles.map((tile, i) => (
          <TileCard
            key={i}
            tile={tile}
            currency={currency}
            variantIndex={i}
            bkiCity={bkiCity}
            bkiSize={bkiSize}
            bkiStandard={bkiStandard}
            inflation={inflation}
          />
        ))}
      </div>
    </div>
  );
});

function TileCard({
  tile,
  currency,
  variantIndex,
  bkiCity,
  bkiSize,
  bkiStandard,
  inflation,
}: {
  tile: Tile;
  currency: string;
  variantIndex: number;
  bkiCity: BkiCity | null;
  bkiSize: BkiSizeBracket;
  bkiStandard: BkiStandard;
  inflation: number;
}) {
  const summary = tile.costMetrics?.summary;
  const arch = tile.item?.architecturalMetrics;
  // Intensity rows are derived per render — re-running cost calcs updates
  // summary.construction, which flows through these without extra state.
  const gia = arch?.GIATotal ?? 0;
  const units = arch?.ApartmentQuantityTotal ?? 0;
  const costPerSqm = summary && gia > 0 ? summary.construction / gia : null;
  const costPerUnit = summary && units > 0 ? summary.construction / units : null;

  // German DIN 276 partial — only when a BKI city is picked. Partial because
  // KG 100 (land / BRW) and KG 800 (financing) aren't covered by BKI.
  const germanTotal = useMemo(() => {
    if (!arch || !bkiCity) return null;
    const calc = computeGerman(
      arch,
      "bgf",
      {},
      { city: bkiCity, sizeBracket: bkiSize, standard: bkiStandard },
      inflation
    );
    return calc.partialTotal;
  }, [arch, bkiCity, bkiSize, bkiStandard, inflation]);

  return (
    <div className="border border-neutral-3 rounded p-3 flex flex-col">
      <div className="flex items-baseline justify-between gap-2 mb-2">
        <div className="flex items-baseline gap-2 min-w-0">
          <h3 className="font-semibold truncate">{tile.variant.label}</h3>
          {tile.variant.kind === "rect" && (
            <span className="px-1.5 py-0.5 rounded bg-secondary-2 text-secondary-7 font-semibold uppercase text-[10px] tracking-wide shrink-0">
              Rect
            </span>
          )}
        </div>
        <Link
          to={`/admin/cost-tracking/${variantIndex}`}
          className="text-neutral-6 hover:text-neutral-8 shrink-0 underline-offset-2 hover:underline"
        >
          {tile.variant.length}×{tile.variant.width}m · {tile.variant.floors}f →
        </Link>
      </div>

      <div className="aspect-square bg-neutral-2 border border-neutral-3 rounded mb-2 flex items-center justify-center overflow-hidden">
        {tile.thumbnail3D ? (
          <img
            src={tile.thumbnail3D}
            alt={`${tile.variant.label} 3D view`}
            className="w-full h-full object-contain"
          />
        ) : (
          <span className="text-neutral-5 italic">{statusLabel(tile)}</span>
        )}
      </div>

      {tile.error ? (
        <p className="text-danger-7 break-words">{tile.error}</p>
      ) : (
        <>
          {arch && (
            <dl className="grid grid-cols-2 gap-x-2 gap-y-1 mb-2">
              <Row label="GIA" value={fmtArch(gia, "m²")} />
              <Row label="Units" value={fmtArch(units)} />
              <Row label="Modules" value={fmtArch(arch.ModuleQuantityTotal ?? 0)} />
            </dl>
          )}
          {summary ? (
            <dl className="grid grid-cols-2 gap-x-2 gap-y-1 border-t border-neutral-3 pt-2">
              <Row label="Construction" value={fmtCost(summary.construction, currency)} />
              <Row label="· /m² GIA" value={fmtCost(costPerSqm, currency)} muted />
              <Row label="· /unit" value={fmtCost(costPerUnit, currency)} muted />
              <Row label="Modules" value={fmtCost(summary.constructionModules, currency)} />
              <Row label="Base build" value={fmtCost(summary.constructionBaseBuild, currency)} />
              <Row label="Fit out" value={fmtCost(summary.constructionFitOut, currency)} />
              <Row label="Total" value={fmtCost(summary.total, currency)} />
              {germanTotal != null && (
                <Row
                  label={`DE partial · ${bkiCity}`}
                  value={fmtCost(germanTotal, "€")}
                />
              )}
            </dl>
          ) : (
            <p className="text-neutral-5 italic">…</p>
          )}
        </>
      )}
    </div>
  );
}

function statusLabel(tile: Tile): string {
  switch (tile.status) {
    case "pending":
      return "Queued";
    case "generating":
      return "Generating…";
    case "rendering":
      return "Rendering…";
    case "error":
      return "Error";
    case "done":
      return "";
  }
}

function Row({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <>
      <dt className={muted ? "text-neutral-5" : "text-neutral-6"}>{label}</dt>
      <dd className={`text-right tabular-nums ${muted ? "text-neutral-6" : ""}`}>{value}</dd>
    </>
  );
}

function fmtCost(value: number | null | undefined, currency: string): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return `${currency}${Math.round(value).toLocaleString()}`;
}

function fmtArch(value: number, suffix?: string): string {
  if (!Number.isFinite(value)) return "—";
  const formatted = Math.round(value).toLocaleString();
  return suffix ? `${formatted} ${suffix}` : formatted;
}
